import Phaser from "phaser";

import { MAX_PLAYERS, MIN_PLAYERS, PHASE_DURATION_MS } from "@shared/constants";
import type { Phase, VoteRejection } from "@shared/types";

import type { RoomState } from "../../server/state/RoomState.ts";
import type { RoomClient } from "../net/RoomClient.ts";
import { installTestHooks, type TestHooks } from "../state/testHooks.ts";
import { AvatarRing, type RingPlayer } from "../ui/AvatarRing.ts";
import { COLORS, FONT, SPACE } from "../ui/theme.ts";
import { TimerBar } from "../ui/TimerBar.ts";

export type GameData = {
  client: RoomClient;
};

const REJECTION_TEXT: Record<VoteRejection, string> = {
  wrong_phase: "Voting is closed",
  self_vote: "You cannot pick yourself",
  unknown_target: "That player is no longer here",
  unknown_voter: "You are not seated at this table",
};

/**
 * The whole round loop in one scene. The avatar ring persists across phases,
 * only the copy, the timer and what tapping does change. All of it is driven
 * by server state: nothing here advances a phase on its own.
 */
export class GameScene extends Phaser.Scene {
  static readonly KEY = "game";

  private _client!: RoomClient;
  private _unsubscribe: Array<() => void> = [];
  private _lastPhase: Phase | null = null;
  private _testHooks: TestHooks | null = null;

  private _ring!: AvatarRing;
  private _timer!: TimerBar;
  private _headline!: Phaser.GameObjects.Text;
  private _prompt!: Phaser.GameObjects.Text;
  private _hint!: Phaser.GameObjects.Text;
  private _toast!: Phaser.GameObjects.Text;
  private _button!: Phaser.GameObjects.Text;
  private _scoreboard!: Phaser.GameObjects.Text;

  constructor() {
    super(GameScene.KEY);
  }

  init(data: GameData): void {
    this._client = data.client;
  }

  create(): void {
    this.cameras.main.setBackgroundColor(COLORS.background);
    const { width, height } = this.scale;
    const textStyle = { fontFamily: FONT.family, color: COLORS.text, align: "center" as const };

    this._headline = this.add
      .text(width / 2, SPACE.lg, "", { ...textStyle, fontSize: `${FONT.small}px`, color: COLORS.textMuted })
      .setOrigin(0.5, 0);
    this._prompt = this.add
      .text(width / 2, SPACE.lg + 28, "", {
        ...textStyle,
        fontSize: `${FONT.prompt}px`,
        fontStyle: "bold",
        wordWrap: { width: width - SPACE.lg * 2 },
      })
      .setOrigin(0.5, 0);
    this._timer = new TimerBar(this, SPACE.lg, 0, width - SPACE.lg * 2);
    this._ring = new AvatarRing(this, width / 2, height / 2, { onSelect: (id) => this._client.vote(id) });
    this._ring.setMyUserId(this._client.myUserId);
    this._hint = this.add
      .text(width / 2, height - SPACE.lg * 3, "", {
        ...textStyle,
        fontSize: `${FONT.body}px`,
        color: COLORS.textMuted,
        wordWrap: { width: width - SPACE.lg * 2 },
      })
      .setOrigin(0.5, 1);
    this._scoreboard = this.add
      .text(width / 2, height / 2, "", { ...textStyle, fontSize: `${FONT.body}px`, lineSpacing: 8 })
      .setOrigin(0.5)
      .setVisible(false);
    this._button = this.add
      .text(width / 2, height - SPACE.lg, "Start game", {
        ...textStyle,
        fontSize: `${FONT.body}px`,
        fontStyle: "bold",
        color: "#1b1530",
        backgroundColor: "#ffb703",
        padding: { x: 24, y: 12 },
      })
      .setOrigin(0.5, 1)
      .setVisible(false)
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => this._client.start());
    this._toast = this.add
      .text(width / 2, height - SPACE.lg * 5, "", {
        ...textStyle,
        fontSize: `${FONT.small}px`,
        color: "#1b1530",
        backgroundColor: "#ff5d73",
        padding: { x: 12, y: 6 },
      })
      .setOrigin(0.5)
      .setVisible(false);

    this._layout(width, height);
    this.scale.on(Phaser.Scale.Events.RESIZE, (size: Phaser.Structs.Size) => this._layout(size.width, size.height));

    this._unsubscribe.push(this._client.onStateChange((state) => this._render(state)));
    this._unsubscribe.push(
      this._client.onMessage("voteRejected", ({ reason }) => {
        this._ring.setSelected(null);
        this._showToast(REJECTION_TEXT[reason]);
      }),
    );
    this._client.onLeave((code) => this._onDisconnected(code));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this._unsubscribe.forEach((stop) => stop()));

    this._testHooks = installTestHooks();
    this._render(this._client.state);
  }

  override update(): void {
    this._timer.tick(Date.now());
  }

  private _layout(width: number, height: number): void {
    const bubble = Phaser.Math.Clamp(Math.min(width, height) * 0.07, 26, 44);
    this._headline.setPosition(width / 2, SPACE.md);
    this._prompt.setPosition(width / 2, SPACE.md + 26).setWordWrapWidth(width - SPACE.lg * 2);
    const timerY = SPACE.md + 26 + this._prompt.height + SPACE.md;
    this._timer.setPosition(SPACE.lg, timerY);
    this._timer.resize(width - SPACE.lg * 2);
    this._hint.setPosition(width / 2, height - SPACE.lg * 3).setWordWrapWidth(width - SPACE.lg * 2);
    this._button.setPosition(width / 2, height - SPACE.lg);
    this._toast.setPosition(width / 2, height - SPACE.lg * 5);

    // The ring fills whatever is left between the timer and the hint, with
    // room for a bubble plus its name label on both the top and bottom edge.
    const labelHeight = FONT.small + SPACE.sm;
    const top = timerY + SPACE.md + bubble;
    const bottom = this._hint.y - this._hint.height - SPACE.md - bubble - labelHeight;
    const centerY = (top + bottom) / 2;
    const radius = Phaser.Math.Clamp(Math.min((bottom - top) / 2, width / 2 - bubble - SPACE.lg), 60, 220);
    this._ring.setPosition(width / 2, centerY);
    this._ring.resize(radius, bubble);
    this._scoreboard.setPosition(width / 2, centerY);
  }

  private _render(state: RoomState): void {
    const players = this._toRingPlayers(state);
    this._ring.setPlayers(players);

    const phaseChanged = state.phase !== this._lastPhase;
    if (phaseChanged) {
      console.info(`[game] phase ${this._lastPhase ?? "none"} -> ${state.phase}, round ${state.roundNumber}`);
      this._lastPhase = state.phase;
      this._timer.setDeadline(state.phaseEndsAt, PHASE_DURATION_MS[state.phase]);
      this._ring.setSelectable(state.phase === "voting");
      this._ring.setShowVoteBadges(state.phase === "voting");
      this._ring.setTally(null);
      this._scoreboard.setVisible(false);
      this._ring.setVisible(true);
    }

    const me = state.players.get(this._client.myUserId);
    const roundLabel = state.roundNumber > 0 ? `Round ${state.roundNumber}` : "";

    switch (state.phase) {
      case "lobby": {
        const missing = Math.max(0, MIN_PLAYERS - players.length);
        const finishedSession = players.some((p) => p.score > 0);
        this._headline.setText(finishedSession ? "Final scores" : "Lobby");
        this._prompt.setText(missing > 0 ? `Waiting for ${missing} more player${missing === 1 ? "" : "s"}` : finishedSession ? "" : "Everyone is here");
        this._hint.setText(missing > 0 ? `Need ${MIN_PLAYERS} to ${MAX_PLAYERS} players in the voice channel` : "");
        this._button.setText(finishedSession ? "Play again" : "Start game").setVisible(missing === 0);
        if (finishedSession) {
          this._ring.setVisible(false);
          this._scoreboard.setText(this._scoreLines(players)).setVisible(true);
        }
        break;
      }
      case "prompt":
        this._headline.setText(`${roundLabel}: read the prompt`);
        this._prompt.setText(state.promptText);
        this._hint.setText("Think about who fits. Voting opens in a moment.");
        this._button.setVisible(false);
        break;
      case "voting":
        this._headline.setText(`${roundLabel}: vote`);
        this._prompt.setText(state.promptText);
        this._hint.setText(me?.hasVoted ? "Locked in. Defend your pick out loud." : "Tap the player you are picking");
        this._button.setVisible(false);
        break;
      case "reveal": {
        this._headline.setText(`${roundLabel}: reveal`);
        this._prompt.setText(state.promptText);
        this._button.setVisible(false);
        const result = state.lastResult;
        if (result && phaseChanged) {
          const tally: Record<string, number> = {};
          result.tally.forEach((count, id) => {
            tally[id] = count;
          });
          const winners = result.winnerUserIds.toArray();
          this._ring.setTally(tally, winners);
          this._hint.setText(this._winnerLine(state, winners));
        }
        break;
      }
      case "scoreboard":
        this._headline.setText(`${roundLabel}: scores`);
        this._prompt.setText("");
        this._hint.setText("Next prompt coming up");
        this._button.setVisible(false);
        this._ring.setVisible(false);
        this._scoreboard.setText(this._scoreLines(players)).setVisible(true);
        break;
    }

    // Prompt height changes between phases, and the ring is sized around it.
    if (phaseChanged) this._layout(this.scale.width, this.scale.height);
    this._publishTestHooks(state, players);
  }

  private _publishTestHooks(state: RoomState, players: RingPlayer[]): void {
    const hooks = this._testHooks;
    if (!hooks) return;
    hooks.phase = state.phase;
    hooks.roundNumber = state.roundNumber;
    hooks.myUserId = this._client.myUserId;
    hooks.promptText = state.promptText;
    hooks.players = players.map((p) => ({
      userId: p.userId,
      hasVoted: p.hasVoted,
      connected: p.connected,
      score: p.score,
      ...(this._ring.bubbleScreenPosition(p.userId) ?? { x: -1, y: -1 }),
    }));
    hooks.startButton = this._button.visible ? { x: this._button.x, y: this._button.y - this._button.height / 2 } : null;
    hooks.lastWinners = state.lastResult?.winnerUserIds.toArray() ?? [];
  }

  private _toRingPlayers(state: RoomState): RingPlayer[] {
    const list: RingPlayer[] = [];
    state.players.forEach((p) => {
      list.push({
        userId: p.userId,
        displayName: p.displayName,
        avatarUrl: p.avatarUrl,
        hasVoted: p.hasVoted,
        connected: p.connected,
        score: p.score,
      });
    });
    return list.sort((a, b) => a.userId.localeCompare(b.userId));
  }

  private _winnerLine(state: RoomState, winners: string[]): string {
    if (winners.length === 0) return "Nobody voted. Tough crowd.";
    const names = winners.map((id) => state.players.get(id)?.displayName ?? "someone");
    if (names.length === 1) return `The table says: ${names[0]}`;
    return `Split decision: ${names.join(" and ")}`;
  }

  private _scoreLines(players: RingPlayer[]): string {
    return [...players]
      .sort((a, b) => b.score - a.score || a.displayName.localeCompare(b.displayName))
      .map((p, i) => `${i + 1}. ${p.displayName}  ${p.score}`)
      .join("\n");
  }

  private _showToast(message: string): void {
    this._toast.setText(message).setVisible(true).setAlpha(1);
    this.tweens.killTweensOf(this._toast);
    this.tweens.add({ targets: this._toast, alpha: 0, delay: 1600, duration: 400, onComplete: () => this._toast.setVisible(false) });
  }

  private _onDisconnected(code: number): void {
    console.warn("[game] left room", code);
    this._hint.setText("Connection lost. Reopen the activity to rejoin.");
    this._ring.setSelectable(false);
    this._button.setVisible(false);
  }
}
